import { api } from '@/lib/api'

/**
 * Validate that an externally-supplied URL is safe to navigate to.
 *
 * This prevents open-redirect attacks where an attacker-supplied URL uses a
 * javascript: protocol, a protocol-relative URL, or an unexpected external
 * origin. Known/trusted origins are the current dashboard origin, the API
 * origin, and DodoPayment checkout domains.
 */
export function isSafeRedirectUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false

  try {
    const parsed = new URL(url, window.location.href)

    // Reject non-HTTP(S) schemes (javascript:, data:, blob:, etc.)
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false
    }

    // Protocol-relative URLs are normalized by the URL constructor using the
    // current page's protocol, but explicitly reject strings that start with
    // '//' just in case.
    if (url.trim().startsWith('//')) {
      return false
    }

    const allowedHosts: string[] = [window.location.hostname]

    // Trust the configured API backend if it is absolute
    const apiBase = api.defaults.baseURL
    if (apiBase) {
      try {
        const apiHost = new URL(apiBase, window.location.href).hostname
        if (apiHost) allowedHosts.push(apiHost)
      } catch {
        // ignore invalid baseURL
      }
    }

    // DodoPayment checkout domains
    allowedHosts.push('checkout.dodopayments.com', 'test.checkout.dodopayments.com')

    // Presigned object-storage URLs (e.g., export downloads, attachments).
    // Set VITE_STORAGE_PUBLIC_ENDPOINT to the same public endpoint used by the
    // API (STORAGE_PUBLIC_ENDPOINT). Example: https://storage.purpletoadmail.com
    const storagePublicEndpoint = import.meta.env.VITE_STORAGE_PUBLIC_ENDPOINT
    if (storagePublicEndpoint) {
      try {
        const storageHost = new URL(storagePublicEndpoint).hostname
        if (storageHost) allowedHosts.push(storageHost)
      } catch {
        // ignore invalid storage endpoint
      }
    }

    const matchesAllowed = allowedHosts.some((host) => {
      if (!host) return false
      return parsed.hostname === host || parsed.hostname.endsWith(`.${host}`)
    })

    return matchesAllowed
  } catch {
    return false
  }
}
